import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Spinner,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Checkbox,
    Input,
    Button,
    Tabs,
    Tab,
} from "@heroui/react";
import Navbar from "../components/Navbar";
import { getVolunteerData, postVolunteerAvailability, getVolunteerAvailability, VolunteerData } from "../api/api";
import toast from 'react-hot-toast';

// Helper function to get next 6 Mondays
const getNextEightMondays = (): Date[] => {
    const mondays: Date[] = [];
    const today = new Date();
    let day = today.getDay();
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + ((8 - day) % 7 || 7)); // get next Monday

    for (let i = 0; i < 8; i++) {
        const monday = new Date(nextMonday);
        monday.setDate(nextMonday.getDate() + i * 7);
        mondays.push(monday);
    }

    return mondays;
};

const Home: React.FC = () => {
    const [assignedCalls, setAssignedCalls] = useState<VolunteerData[]>([]);
    const [futureCalls, setFutureAssignedCalls] = useState<VolunteerData[]>([]);
    const [pastCalls, setPastCalls] = useState<VolunteerData[]>([]);
    const [, setAvailability] = useState<any[]>([]);
    const [saving, setSaving] = useState<boolean>(false);
    const [availabilityRows, setAvailabilityRows] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState("current");

    const handleSave = () => {
        const formattedPreferences = availabilityRows.map((row) => ({
            date: row.date.toISOString().split("T")[0],
            available: row.available,
            maxCalls: row.maxCalls,
        }));
    
        setSaving(true);
    
        toast.promise(
            postVolunteerAvailability(formattedPreferences),
            {
                loading: "Saving availability...",
                success: "Availability saved successfully!",
                error: "Error saving availability.",
            }
        ).finally(() => {
            setSaving(false);
        });
    
        console.log("Weekly Call Preferences:", formattedPreferences);
    };
    

    useEffect(() => {
        const getData = async () => {
            setLoading(true);

            try {
                const volunteerData: any = await getVolunteerData();
                setAssignedCalls(volunteerData.current_assigned_calls);
                setFutureAssignedCalls(volunteerData.future_assigned_calls);
                setPastCalls(volunteerData.past_assigned_calls);

                const availabilityData: any = await getVolunteerAvailability();
                setAvailability(availabilityData);

                const formattedRows = getNextEightMondays().map((monday) => {
                    const found = availabilityData.find(
                        (entry: any) => new Date(entry.date).toDateString() === monday.toDateString()
                    );

                    return {
                        date: monday,
                        available: found ? found.available : false,
                        maxCalls: found ? found.maxCalls : 5,
                    };
                });

                setAvailabilityRows(formattedRows);
            } catch (error) {
                console.error("Error fetching data:", error);
            }

            setLoading(false);
        };

        getData();
    }, []);

    const keyToCallMapping: Record<string, VolunteerData[]> = {
        "current": assignedCalls,
        "future": futureCalls,
        "past": pastCalls
    }

    return (
        <div className="w-screen min-h-screen bg-white flex flex-col relative">
            <Navbar />
            <div className="w-full min-h-[3px] bg-yorkOrange"></div>
            {(loading || saving) && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
                    <Spinner size="lg" color="primary" />
                </div>
            )}

            <main className={`flex flex-col items-center justify-start p-4 ${loading ? "opacity-50 pointer-events-none" : ""}`}>
                <div className="pb-4 text-xl"><p>Welcome to the York Neighbours Volunteer Portal!</p></div>
                <div className="flex flex-col gap-4 w-full max-w-screen-lg" style={{ height: "calc(80vh)" }}>
                <Tabs
                    aria-label="Call Schedule Tabs"
                    className="justify-center"
                    classNames={{tab: "bg-gray-50"}}
                    selectedKey={activeTab}
                    onSelectionChange={(key) => setActiveTab(key as string)}
                    items={[
                        { key: "current", title: "This Weeks Calls" },
                        { key: "future", title: "Next Weeks Calls" },
                        { key: "past", title: "Last Weeks Calls" },
                        { key: "availability", title: "My Availability" },
                    ]}
                >
                    {(item) => (
                        <Tab key={item.key} title={item.title}>
                            {item.key != 'availability' ? (
                                <Table aria-label={`${item.title} Table`}>
                                    <TableHeader>
                                        <TableColumn>NEIGHBOURS NAME</TableColumn>
                                        <TableColumn>HOME</TableColumn>
                                        <TableColumn>MOBILE</TableColumn>
                                        <TableColumn>STATUS</TableColumn>
                                    </TableHeader>
                                    <TableBody items={keyToCallMapping[item.key]}>
                                        {(call) => (
                                            <TableRow key={call.id}>
                                                <TableCell>
                                                    <Link to={`/record/${call.id}`} className="text-blue-600 hover:underline">
                                                        {call.neighbour.neighbour}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>{call.neighbour.homephone}</TableCell>
                                                <TableCell>{call.neighbour.mobilephone}</TableCell>
                                                <TableCell>{call.eventstatus}</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            ) : (
                                <>
                                    <div className="border rounded-xl p-4 shadow-sm bg-white w-full">
                                    <Table removeWrapper aria-label="Weekly Availability and Call Limits">
                                        <TableHeader>
                                            <TableColumn style={{ width: "35%" }}>WEEK COMMENCING</TableColumn>
                                            <TableColumn style={{ width: "30%" }}><center>AVAILABLE</center></TableColumn>
                                            <TableColumn style={{ width: "35%" }}><center>MAXIMUM CALLS</center></TableColumn>
                                        </TableHeader>
                                        <TableBody items={availabilityRows}>
                                            {(row) => (
                                                <TableRow key={row.date.toISOString()}>
                                                    <TableCell>
                                                        {row.date.toLocaleDateString("en-GB", {
                                                            weekday: "short",
                                                            day: "numeric",
                                                            month: "short",
                                                            year: "numeric",
                                                        })}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex justify-center">
                                                        <Checkbox
                                                            isSelected={row.available}
                                                            onChange={(event) => {
                                                                const checked = event.target.checked;
                                                                setAvailabilityRows((prev) =>
                                                                    prev.map((r) =>
                                                                        r.date.getTime() === row.date.getTime()
                                                                            ? { ...r, available: checked }
                                                                            : r
                                                                    )
                                                                );
                                                            }}
                                                        />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                    <div className="flex justify-center">
                                                        <Input
                                                            type="number"
                                                            min={1}
                                                            value={row.maxCalls.toString()}
                                                            onChange={(e) => {
                                                                const newValue = parseInt(e.target.value || "0", 10);
                                                                setAvailabilityRows((prev) =>
                                                                prev.map((r) =>
                                                                    r.date.getTime() === row.date.getTime()
                                                                    ? { ...r, maxCalls: newValue }
                                                                    : r
                                                                )
                                                                );
                                                            }}
                                                            className="w-32"
                                                            classNames={{
                                                                inputWrapper: "bg-white",
                                                                input: "text-black text-center", // optional, to ensure text is readable
                                                            }}
                                                        />
                                                    </div>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                    <div className="flex items-center justify-end pt-2">
                                        <Button onClick={handleSave} color="primary">
                                            Save Availability
                                        </Button>
                                    </div>
                                    </div>
                                </>
                            )}
                        </Tab>
                    )}
                </Tabs>
                <div className="pb-10" />
                </div>
            </main>
        </div>
    );
};

export default Home;
